/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import createDOMPurify from "dompurify";
import { AiOutlineLoading } from "react-icons/ai";
import {
  HiOutlineClock,
  HiOutlineExclamation,
  HiOutlineFolder,
  HiOutlinePause,
  HiOutlinePencil,
  HiOutlinePlay,
  HiOutlineSearch,
  HiOutlineTrash,
  HiOutlineX,
} from "react-icons/hi";

import Service from "../../api/service";
import { BreadCrumbComponent } from "../../ui/BreadCrumbComponent";
import { pushCountry } from "../../lib/sysNavigation/SysNavigation";
import { useAdminContext } from "../../context/admin/AdminContext";
import { useDoSeeDataContext } from "../../context/DoSeeDataContext";
import {
  Button,
  Card,
  ConfirmDialog,
  IconButton,
  cn,
} from "../../ui";
import { getType } from "../contentStats/helper";
// @ts-ignore — local JS barrel maps localSearch payload → section metadata.
import { getContentMasterSearchParams } from ".";

// ─── Types ────────────────────────────────────────────────────────────
type SearchItem = Record<string, any>;

type Section = {
  title: string;
  data?: SearchItem[];
  route: string;
  idLabel: string;
  titleLabel?: string;
  collection: string;
  type: string;
  pushMethod: (row: any) => void;
};

type StatusFilter = "all" | "active" | "suspended";
type SortBy = "relevance" | "az" | "za";

type ConfirmState =
  | { kind: "delete"; item: SearchItem; section: Section }
  | { kind: "edit"; item: SearchItem; section: Section }
  | { kind: "toggle"; item: SearchItem; section: Section; nextActive: boolean }
  | null;

// ─── Constants & helpers ──────────────────────────────────────────────
const DOMPurify =
  typeof window !== "undefined"
    ? createDOMPurify(window as unknown as Parameters<typeof createDOMPurify>[0])
    : null;

const EXAMPLE_QUERIES = [
  "safari",
  "Nairobi hotels",
  "music festival",
  "restaurant",
  "city guide",
];

const RECENTS_KEY = "search.recent";
const RECENT_LIMIT = 6;

const NON_CONTENT_KEYS = new Set([
  "Company",
  "FirstLevel",
  "SecondLevel",
  "ThirdLevel",
  "FourthLevel",
]);

const stripHtml = (html?: string): string => {
  if (!html) return "";
  if (!DOMPurify) return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const clean = DOMPurify.sanitize(html);
  const div = document.createElement("div");
  div.innerHTML = clean;
  return (div.textContent ?? div.innerText ?? "")
    .replace(/\s+/g, " ")
    .trim();
};

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const readRecents = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
};

const writeRecents = (items: string[]) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      RECENTS_KEY,
      JSON.stringify(items.slice(0, RECENT_LIMIT)),
    );
  } catch {
    /* ignore */
  }
};

// ─── Result card ──────────────────────────────────────────────────────
type ResultCardProps = {
  item: SearchItem;
  section: Section;
  index: number;
  onEditAsk: () => void;
  onDeleteAsk: () => void;
  onToggleAsk: () => void;
  actingKind: "delete" | "toggle" | null;
};

const ResultCard: React.FC<ResultCardProps> = ({
  item,
  section,
  index,
  onEditAsk,
  onDeleteAsk,
  onToggleAsk,
  actingKind,
}) => {
  const title = String(
    (section.titleLabel && item[section.titleLabel]) ?? item.title ?? "Untitled",
  );
  const isActive = item.status === 1;
  const preview = stripHtml(item.description as string | undefined);

  return (
    <article className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-[0_10px_30px_-12px_rgba(0,145,145,0.18)]">
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-brand transition-transform duration-300 ease-out group-hover:scale-x-100"
      />

      <header className="flex items-start gap-3">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-[11px] font-semibold text-brand ring-1 ring-inset ring-brand/15">
          {index}
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-[14px] font-semibold tracking-tight text-gray-900">
            {title}
          </h4>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10.5px] font-semibold text-gray-600">
              <HiOutlineFolder className="h-3 w-3" />
              {section.title}
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset",
                isActive
                  ? "bg-brand/5 text-brand ring-brand/15"
                  : "bg-amber-50 text-amber-700 ring-amber-200",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  isActive ? "bg-brand" : "bg-amber-500",
                )}
              />
              {isActive ? "Active" : "Suspended"}
            </span>
          </div>
        </div>
      </header>

      {preview ? (
        <p className="mt-3 line-clamp-3 text-[12.5px] leading-relaxed text-gray-600">
          {preview}
        </p>
      ) : (
        <p className="mt-3 text-[12px] italic text-gray-400">No description.</p>
      )}

      <footer className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
        <Button variant="primary" size="sm" onClick={onEditAsk}>
          <HiOutlinePencil className="h-3.5 w-3.5" />
          Edit
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onToggleAsk}
          disabled={actingKind === "toggle"}
        >
          {actingKind === "toggle" ? (
            <AiOutlineLoading className="h-3.5 w-3.5 animate-spin" />
          ) : isActive ? (
            <HiOutlinePause className="h-3.5 w-3.5" />
          ) : (
            <HiOutlinePlay className="h-3.5 w-3.5" />
          )}
          {isActive ? "Suspend" : "Unsuspend"}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={onDeleteAsk}
          disabled={actingKind === "delete"}
        >
          {actingKind === "delete" ? (
            <AiOutlineLoading className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <HiOutlineTrash className="h-3.5 w-3.5" />
          )}
          Delete
        </Button>
      </footer>
    </article>
  );
};

// ─── Main page ────────────────────────────────────────────────────────
const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { sysAdmin, setSingleAdmin } = useAdminContext();
  const { deleteOneContent, updateOneContentStatus } =
    useDoSeeDataContext() as any;

  const [keyword, setKeyword] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState<Record<string, SearchItem[]> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const [sectionFilter, setSectionFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortBy, setSortBy] = useState<SortBy>("relevance");
  const [recents, setRecents] = useState<string[]>(() => readRecents());

  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [actingKey, setActingKey] = useState<{
    key: string;
    kind: "delete" | "toggle";
  } | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);

  // Run the API call.
  const runSearch = useCallback(
    async (term: string) => {
      const trimmed = term.trim();
      if (!trimmed) return;
      setLoading(true);
      setError(null);
      setSubmitted(trimmed);
      try {
        const { data } = (await Service.get(
          `/api/v1/search/?search_key=${encodeURIComponent(trimmed)}&origin=admin`,
        )) as any;
        setPayload((data as Record<string, SearchItem[]>) ?? {});
      } catch {
        setError("Couldn't reach the search service. Please try again.");
      } finally {
        setLoading(false);
      }
      setRecents((prev) => {
        const next = [trimmed, ...prev.filter((t) => t !== trimmed)].slice(
          0,
          RECENT_LIMIT,
        );
        writeRecents(next);
        return next;
      });
    },
    [],
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSearch(keyword);
  };

  const clearAll = () => {
    setKeyword("");
    setPayload(null);
    setSubmitted(null);
    setError(null);
    setSectionFilter("all");
    searchRef.current?.focus();
  };

  const clearRecents = () => {
    writeRecents([]);
    setRecents([]);
  };

  // Keyboard: `/` focuses the search when not already in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA");
      if (!inField && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ─── Derived ──────────────────────────────────────────────────────
  const sections: Section[] = useMemo(() => {
    if (!payload) return [];
    return getContentMasterSearchParams(payload) as Section[];
  }, [payload]);

  const totalResults = useMemo(() => {
    if (!payload) return 0;
    let total = 0;
    for (const key of Object.keys(payload)) {
      if (NON_CONTENT_KEYS.has(key)) continue;
      total += payload[key]?.length ?? 0;
    }
    return total;
  }, [payload]);

  const applyFiltersAndSort = useCallback(
    (arr: SearchItem[] = [], titleLabel = "title") => {
      let items = Array.isArray(arr) ? [...arr] : [];
      if (statusFilter !== "all") {
        const want = statusFilter === "active" ? 1 : 0;
        items = items.filter((i) =>
          typeof i?.status === "number" ? i.status === want : true,
        );
      }
      const compare = (a: SearchItem, b: SearchItem) =>
        String(a?.[titleLabel] ?? a?.title ?? "").localeCompare(
          String(b?.[titleLabel] ?? b?.title ?? ""),
          undefined,
          { sensitivity: "base" },
        );
      if (sortBy === "az") items.sort(compare);
      else if (sortBy === "za") items.sort((a, b) => compare(b, a));
      return items;
    },
    [statusFilter, sortBy],
  );

  const sectionsWithResults = useMemo(() => {
    return sections
      .map((section) => {
        const filtered = applyFiltersAndSort(
          section.data ?? [],
          section.titleLabel || "title",
        );
        return { section, filtered };
      })
      .filter(({ filtered }) => filtered.length > 0);
  }, [sections, applyFiltersAndSort]);

  const visibleSections = useMemo(() => {
    if (sectionFilter === "all") return sectionsWithResults;
    return sectionsWithResults.filter(
      ({ section }) => section.title === sectionFilter,
    );
  }, [sectionsWithResults, sectionFilter]);

  const visibleResultCount = useMemo(
    () => visibleSections.reduce((sum, g) => sum + g.filtered.length, 0),
    [visibleSections],
  );

  // ─── Actions ──────────────────────────────────────────────────────
  const itemKey = (section: Section, item: SearchItem) =>
    `${section.collection}:${item[section.idLabel]}`;

  const doEdit = (section: Section, item: SearchItem) => {
    section.pushMethod(item);
    pushCountry({
      country: { id: 0, name: "", slug: "", route: "/search" },
    } as any);
    setSingleAdmin({
      ids: [(sysAdmin as any)?.admin_id],
      type: getType({ field: section.collection }),
      route: "/search",
    } as any);
    navigate(section.route);
  };

  const doDelete = async (section: Section, item: SearchItem) => {
    const key = itemKey(section, item);
    setActingKey({ key, kind: "delete" });
    try {
      const { status } = await deleteOneContent({
        data_id: item[section.idLabel],
        content: section.collection,
      });
      if (status === 200 && submitted) {
        await runSearch(submitted);
      }
    } finally {
      setActingKey(null);
    }
  };

  const doToggle = async (
    section: Section,
    item: SearchItem,
    nextActive: boolean,
  ) => {
    const key = itemKey(section, item);
    setActingKey({ key, kind: "toggle" });
    try {
      const { status } = await updateOneContentStatus({
        id: item[section.idLabel],
        type: section.type,
        statusState: nextActive ? 1 : 0,
      });
      if (status === 200) {
        // Patch the local payload so the UI updates without a round-trip.
        setPayload((prev) => {
          if (!prev) return prev;
          const clone = { ...prev };
          for (const k of Object.keys(clone)) {
            clone[k] = clone[k].map((row) =>
              row[section.idLabel] === item[section.idLabel]
                ? { ...row, status: nextActive ? 1 : 0 }
                : row,
            );
          }
          return clone;
        });
      }
    } finally {
      setActingKey(null);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { item, section } = confirm;
    if (confirm.kind === "delete") await doDelete(section, item);
    else if (confirm.kind === "edit") doEdit(section, item);
    else if (confirm.kind === "toggle")
      await doToggle(section, item, confirm.nextActive);
    setConfirm(null);
  };

  // ─── Render helpers ───────────────────────────────────────────────
  const statusPills: { key: StatusFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "active", label: "Active" },
    { key: "suspended", label: "Suspended" },
  ];
  const sortPills: { key: SortBy; label: string }[] = [
    { key: "relevance", label: "Relevance" },
    { key: "az", label: "A → Z" },
    { key: "za", label: "Z → A" },
  ];

  const confirmTitle =
    confirm?.kind === "delete"
      ? "Delete this content?"
      : confirm?.kind === "edit"
        ? "Open this item for editing?"
        : confirm?.kind === "toggle"
          ? confirm.nextActive
            ? "Make this visible again?"
            : "Suspend this content?"
          : "";
  const confirmDescription =
    confirm?.kind === "delete"
      ? "This will permanently remove the item. It can't be recovered."
      : confirm?.kind === "edit"
        ? "You'll be taken to the editor for this item."
        : confirm?.kind === "toggle"
          ? confirm.nextActive
            ? "Visitors will be able to see it on the public site again."
            : "It will be hidden from the public site until re-activated."
          : "";
  const confirmLabel =
    confirm?.kind === "delete"
      ? "Delete"
      : confirm?.kind === "edit"
        ? "Open editor"
        : confirm?.kind === "toggle"
          ? confirm.nextActive
            ? "Unsuspend"
            : "Suspend"
          : "Confirm";

  return (
    <div className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-gray-50">
      <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6">
        <BreadCrumbComponent />

        {/* ── Hero search ──────────────────────────────────────── */}
        <section className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand/10 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 left-20 h-48 w-48 rounded-full bg-brand/[0.07] blur-3xl"
          />

          <div className="relative px-6 py-7 sm:px-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand">
              <span aria-hidden className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand/60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-brand" />
              </span>
              Global search
            </div>

            <h1 className="mt-4 text-[26px] font-semibold leading-tight tracking-tight text-gray-900 sm:text-[30px]">
              Search across every section
            </h1>
            <p className="mt-2 max-w-2xl text-[13.5px] text-gray-500">
              Events, hotels, safaris, cities, ministries — find and manage any
              record from one place.
            </p>

            <form onSubmit={onSubmit} className="mt-5">
              <div className="relative rounded-2xl ring-1 ring-gray-200 transition-all focus-within:ring-2 focus-within:ring-brand/30">
                <HiOutlineSearch
                  aria-hidden
                  className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-brand"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="e.g. safari, Nairobi hotels, music festival…"
                  autoFocus
                  className="h-14 w-full rounded-2xl border-0 bg-white pl-12 pr-36 text-[15px] text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  aria-label="Search content"
                />
                <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
                  <kbd className="hidden rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 sm:inline-block">
                    /
                  </kbd>
                  {keyword ? (
                    <button
                      type="button"
                      onClick={() => setKeyword("")}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-brand/5 hover:text-brand"
                      aria-label="Clear search"
                    >
                      <HiOutlineX className="h-4 w-4" />
                    </button>
                  ) : null}
                  <Button type="submit" variant="primary" disabled={loading || !keyword.trim()}>
                    {loading ? (
                      <AiOutlineLoading className="h-4 w-4 animate-spin" />
                    ) : (
                      "Search"
                    )}
                  </Button>
                </div>
              </div>
            </form>

            <div className="mt-4 space-y-3">
              {recents.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-500">
                    <HiOutlineClock className="h-3 w-3" />
                    Recent
                  </span>
                  {recents.map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => {
                        setKeyword(term);
                        runSearch(term);
                      }}
                      className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-[11.5px] font-medium text-gray-700 transition-colors hover:border-brand/30 hover:bg-brand/5 hover:text-brand"
                    >
                      {term}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={clearRecents}
                    className="text-[11px] font-semibold text-gray-500 underline-offset-2 hover:text-brand hover:underline"
                  >
                    Clear
                  </button>
                </div>
              ) : null}

              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-500">
                  Try
                </span>
                {EXAMPLE_QUERIES.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => {
                      setKeyword(ex);
                      runSearch(ex);
                    }}
                    className="inline-flex items-center rounded-full bg-gray-50 px-3 py-1 text-[11.5px] font-medium text-gray-700 transition-colors hover:bg-brand/5 hover:text-brand"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Error banner ─────────────────────────────────────── */}
        {error ? (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-[13px] text-red-700"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-600 text-white">
              <HiOutlineExclamation className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1 pt-1 font-medium">{error}</div>
          </div>
        ) : null}

        {/* ── Body ─────────────────────────────────────────────── */}
        {loading && !payload ? (
          <LoadingSkeleton />
        ) : !submitted || !payload ? (
          <EmptyState />
        ) : totalResults === 0 ? (
          <NoResultsState keyword={submitted} onClear={clearAll} />
        ) : (
          <>
            {/* Summary + controls */}
            <Card padding="md" className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[13px] font-semibold tracking-tight text-gray-900">
                    {visibleResultCount.toLocaleString()} result
                    {visibleResultCount === 1 ? "" : "s"}
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-gray-500">
                    Searching for{" "}
                    <span className="font-semibold text-gray-800">
                      “{submitted}”
                    </span>{" "}
                    · {sectionsWithResults.length} section
                    {sectionsWithResults.length === 1 ? "" : "s"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearAll}
                  className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-gray-500 underline-offset-2 hover:text-brand hover:underline"
                >
                  <HiOutlineX className="h-3.5 w-3.5" />
                  Clear results
                </button>
              </div>

              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <FilterRow label="Status">
                  {statusPills.map((s) => (
                    <Pill
                      key={s.key}
                      active={statusFilter === s.key}
                      onClick={() => setStatusFilter(s.key)}
                    >
                      {s.label}
                    </Pill>
                  ))}
                </FilterRow>
                <FilterRow label="Sort by">
                  {sortPills.map((s) => (
                    <Pill
                      key={s.key}
                      active={sortBy === s.key}
                      onClick={() => setSortBy(s.key)}
                    >
                      {s.label}
                    </Pill>
                  ))}
                </FilterRow>
              </div>

              {sectionsWithResults.length > 1 ? (
                <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                  <Pill
                    active={sectionFilter === "all"}
                    onClick={() => setSectionFilter("all")}
                  >
                    All sections
                    <Count value={visibleResultCount} active={sectionFilter === "all"} />
                  </Pill>
                  {sectionsWithResults.map(({ section, filtered }) => (
                    <Pill
                      key={section.title}
                      active={sectionFilter === section.title}
                      onClick={() => setSectionFilter(section.title)}
                    >
                      {section.title}
                      <Count
                        value={filtered.length}
                        active={sectionFilter === section.title}
                      />
                    </Pill>
                  ))}
                </div>
              ) : null}
            </Card>

            {/* Section groups */}
            <div className="space-y-10">
              {visibleSections.map(({ section, filtered }) => (
                <section
                  key={section.title}
                  id={`section-${slugify(section.title)}`}
                  aria-label={section.title}
                >
                  <header className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <span
                        aria-hidden
                        className="h-4 w-[3px] rounded-full bg-brand"
                      />
                      <h3 className="truncate text-[15px] font-semibold tracking-tight text-gray-900">
                        {section.title}
                      </h3>
                      <span className="inline-flex h-5 min-w-[1.5rem] items-center justify-center rounded-full bg-gray-100 px-1.5 text-[10.5px] font-semibold tabular-nums text-gray-600">
                        {filtered.length}
                      </span>
                    </div>
                  </header>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {filtered.map((item, i) => {
                      const key = itemKey(section, item);
                      return (
                        <ResultCard
                          key={`${key}-${i}`}
                          item={item}
                          section={section}
                          index={i + 1}
                          onEditAsk={() =>
                            setConfirm({ kind: "edit", item, section })
                          }
                          onDeleteAsk={() =>
                            setConfirm({ kind: "delete", item, section })
                          }
                          onToggleAsk={() =>
                            setConfirm({
                              kind: "toggle",
                              item,
                              section,
                              nextActive: item.status !== 1,
                            })
                          }
                          actingKind={
                            actingKey?.key === key ? actingKey.kind : null
                          }
                        />
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel={confirmLabel}
        destructive={confirm?.kind === "delete"}
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirm}
      />
    </div>
  );
};

// ─── Small building blocks ────────────────────────────────────────────
const Pill: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold tracking-tight transition-colors",
      active
        ? "border-brand bg-brand text-white"
        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
    )}
  >
    {children}
  </button>
);

const Count: React.FC<{ value: number; active: boolean }> = ({
  value,
  active,
}) => (
  <span
    className={cn(
      "inline-flex h-4 min-w-[1.1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums",
      active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600",
    )}
  >
    {value}
  </span>
);

const FilterRow: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-gray-500">
      {label}
    </span>
    {children}
  </div>
);

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-6">
    <Card padding="md">
      <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
      <div className="mt-2 h-3 w-64 animate-pulse rounded bg-gray-100" />
    </Card>
    <div className="space-y-6">
      {[0, 1].map((s) => (
        <div key={s}>
          <div className="mb-3 flex items-center gap-2.5">
            <span className="h-4 w-[3px] rounded-full bg-brand/30" />
            <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="min-h-[150px] animate-pulse rounded-2xl border border-gray-200 bg-white"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <Card padding="md" className="border-dashed text-center">
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
      <HiOutlineSearch className="h-5 w-5" />
    </div>
    <p className="mt-4 text-[13px] font-semibold text-gray-900">
      Start typing to search
    </p>
    <p className="mt-1 text-[12px] text-gray-500">
      Results appear instantly and are grouped by section.
    </p>
  </Card>
);

const NoResultsState: React.FC<{ keyword: string; onClear: () => void }> = ({
  keyword,
  onClear,
}) => (
  <Card padding="md" className="border-dashed text-center">
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500">
      <HiOutlineSearch className="h-5 w-5" />
    </div>
    <p className="mt-4 text-[13px] font-semibold text-gray-900">
      No results for “{keyword}”
    </p>
    <p className="mt-1 text-[12px] text-gray-500">
      Double-check the spelling or try a broader term.
    </p>
    <div className="mt-4 inline-flex">
      <IconButton variant="ghost" onClick={onClear} aria-label="Clear">
        <HiOutlineX className="h-4 w-4" />
      </IconButton>
    </div>
  </Card>
);

export default Overview;
